import smartReportsAxios from '../api/smartReportsAxios';
import { ensureAuthenticated } from './smartReportsAuthService';
import {
  SmartReport,
  UploadResponse,
  JobStatus,
  MyReportsResponse,
  WalletBalance,
  CanAffordResponse,
  SmartReportSummary,
  SRCreateOrderResponse,
  SRPaymentConfig,
  SRTransactionListResponse,
} from '../types/smartReports';

const smartReportsService = {
  /**
   * Ensure auth before any API call.
   */
  async ensureAuth(): Promise<boolean> {
    return ensureAuthenticated();
  },

  /**
   * Upload a blood report PDF for AI analysis.
   */
  async uploadReport(file: File, useAiVision: boolean = true): Promise<UploadResponse> {
    await ensureAuthenticated();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('use_ai_vision', String(useAiVision));

    const { data } = await smartReportsAxios.post<UploadResponse>('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return data;
  },

  /**
   * Poll job status. Status progression:
   * pending → extracting → analyzing → generating → completed | failed
   */
  async getJobStatus(jobId: string): Promise<JobStatus> {
    const { data } = await smartReportsAxios.get<JobStatus>(`/status/${jobId}`);
    return data;
  },

  /**
   * Get the full report data once status is 'completed'.
   */
  async getReport(jobId: string): Promise<SmartReport> {
    await ensureAuthenticated();
    const { data } = await smartReportsAxios.get<SmartReport>(`/report/${jobId}`);
    return data;
  },

  /**
   * Get a brief summary of a report.
   */
  async getReportSummary(jobId: string): Promise<SmartReportSummary> {
    await ensureAuthenticated();
    const { data } = await smartReportsAxios.get<SmartReportSummary>(`/report/${jobId}/summary`);
    return data;
  },

  /**
   * Download the report as a PDF file.
   */
  async downloadReportPdf(jobId: string): Promise<void> {
    await ensureAuthenticated();
    const response = await smartReportsAxios.get(`/report/${jobId}/pdf`, {
      responseType: 'blob',
    });
    // Trigger browser download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `smart_report_${jobId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * List the current user's reports (paginated).
   */
  async getMyReports(page: number = 1, perPage: number = 10): Promise<MyReportsResponse> {
    await ensureAuthenticated();
    const { data } = await smartReportsAxios.get<MyReportsResponse>('/reports/my-reports', {
      params: { page, per_page: perPage },
    });
    return data;
  },

  /**
   * Get wallet credit balance.
   */
  async getWalletBalance(): Promise<WalletBalance> {
    await ensureAuthenticated();
    const { data } = await smartReportsAxios.get<WalletBalance>('/wallet/balance');
    return data;
  },

  /**
   * Check if user can afford a report analysis.
   */
  async canAffordReport(): Promise<CanAffordResponse> {
    await ensureAuthenticated();
    const { data } = await smartReportsAxios.get<CanAffordResponse>('/wallet/can-afford-report');
    return data;
  },

  /**
   * Get PayU payment configuration.
   */
  async getPaymentConfig(): Promise<SRPaymentConfig> {
    await ensureAuthenticated();
    const { data } = await smartReportsAxios.get<SRPaymentConfig>('/payments/config');
    return data;
  },

  /**
   * Create a PayU payment order to add credits.
   */
  async createPaymentOrder(amount: number, firstname: string, email: string, phone: string = ''): Promise<SRCreateOrderResponse> {
    await ensureAuthenticated();
    const { data } = await smartReportsAxios.post<SRCreateOrderResponse>('/payments/create-order', {
      amount,
      firstname,
      email,
      phone,
    });
    return data;
  },

  /**
   * Get wallet transaction history.
   */
  async getTransactions(page: number = 1, perPage: number = 20): Promise<SRTransactionListResponse> {
    await ensureAuthenticated();
    const { data } = await smartReportsAxios.get<SRTransactionListResponse>('/wallet/transactions', {
      params: { page, per_page: perPage },
    });
    return data;
  },
};

export default smartReportsService;
